// Learner-mode guided walkthroughs. Each step's `check` is a pure function over
// the session log + current ECU state, so progress is derived on every render
// rather than tracked as separate mutable "did they do it" flags.
const respOk = (log, sid, subFn) => log.some((e) => e.direction === 'response' && e.meta?.positive && e.meta.sid === sid && (subFn === undefined || e.meta.subFn === subFn));

export const SCENARIOS = [
  {
    id: 'read-vin',
    title: 'Read the VIN',
    description: 'Use Read Data By Identifier (0x22) to read the vehicle\'s VIN from DID 0xF190.',
    steps: [
      {
        instruction: 'Select "0x22 Read Data By Identifier", choose the VIN (0xF190) DID, and click Send.',
        hint: 'Guided mode → Service dropdown → 0x22 Read Data By Identifier → DID → VIN (0xF190) → Send. This works in any session, no security needed.',
        check: (log) => log.some((e) => e.direction === 'response' && e.meta?.positive && e.meta.sid === 0x22 && e.meta.did === 0xf190),
      },
    ],
    successMessage: 'You read the VIN with a single Read Data By Identifier request — no session change or security needed, since 0x22 is available in any diagnostic session.',
  },
  {
    id: 'unlock-and-clear-dtc',
    title: 'Unlock Security and Clear a DTC',
    description: 'Enter Extended Session, perform the seed/key exchange, then clear diagnostic information.',
    steps: [
      {
        instruction: 'Select "0x10 Diagnostic Session Control" and choose Extended Diagnostic Session (0x03), then Send.',
        hint: 'Writing and erasing services are only allowed outside the Default Session — Extended Session unlocks them.',
        check: (log) => respOk(log, 0x10, 0x03),
      },
      {
        instruction: 'Select "0x27 Security Access", sub-function "Request Seed" (0x01), and Send.',
        hint: 'The ECU will reply with a 2-byte seed you\'ll need for the next step.',
        check: (log) => respOk(log, 0x27, 0x01),
      },
      {
        instruction: 'Compute the key from the seed using the documented algorithm (KEY = SEED XOR 0x2571 by default), then send it with sub-function "Send Key" (0x02).',
        hint: 'Look at the seed in the last response, XOR it with the algorithm value shown in the Reference panel\'s Security Access section, and enter the 4-hex-digit result as the key.',
        check: (log, state) => state.securityUnlocked || respOk(log, 0x27, 0x02),
      },
      {
        instruction: 'Select "0x14 Clear Diagnostic Information" and Send.',
        hint: 'Clearing diagnostic information does not require security to be unlocked, but doing it now shows the full realistic flow: session → security → service.',
        check: (log) => respOk(log, 0x14),
      },
    ],
    successMessage: 'You completed the full realistic flow: Extended Session → seed/key exchange → Clear Diagnostic Information. Try reading DTCs again (0x19 02) to confirm fault memory is empty.',
  },
];

export function getScenarioProgress(scenario, log, state) {
  let currentStep = scenario.steps.length;
  for (let i = 0; i < scenario.steps.length; i += 1) {
    if (!scenario.steps[i].check(log, state)) {
      currentStep = i;
      break;
    }
  }
  return { currentStep, complete: currentStep === scenario.steps.length };
}
